<ng-container *ngIf="permissionService.roleAbility$ | async as roleAbility">
  <h2 mat-dialog-title>
    @if (employee) {
      {{ 'actions.edit_employee' | translate }}
    } @else {
      {{ 'actions.add_employee' | translate }}
    }
    <span class="icon-close icon-Cross" mat-dialog-close></span>
  </h2>
  <mat-dialog-content>
    <ng-container *ngIf="data$ | async as data">
      <div class="employee">
        <form class="form-modal" [formGroup]="form">
          <div class="employee__main-info">
            <div class="employee__main-info__img">
              <app-picture-upload
                [fileExtensions]="RULES.EMPLOYEE_PFP_EXTENSIONS"
                [fileMaxSize]="RULES.IMG_MAX_SIZE_MB * 1024 * 1024"
                [pictureUrl]="data.pictureLink"
                (upload)="uploadPhoto($event)"
                (error)="uploadError($event)"
                #pictureUpload>
                <img [src]="pictureUpload.pictureUrl || './assets/images/default-user-image.svg'" alt="" />
                <ng-container *ngIf="isEditingOwnProfile || roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE)">
                  <div
                    *ngIf="!pictureUpload.pictureUrl"
                    (click)="pictureUpload.selectFile()"
                    class="upload-btn">
                    <span class="icon-Plus grey-button"></span>
                  </div>
                  <div
                    *ngIf="pictureUpload.pictureUrl"
                    (click)="[removePhoto(), pictureUpload.reset()]"
                    class="upload-btn">
                    <span class="icon-Remove"></span>
                  </div>
                </ng-container>
              </app-picture-upload>
            </div>

            <div class="employee__main-info__data">
              <div class="form-modal__inline-fields">
                <mat-form-field>
                  <mat-label>{{ 'form.fields.first_name' | translate }}</mat-label>
                  <input formControlName="firstName" matInput [placeholder]="'form.fields.first_name_placeholder' | translate" trim="blur" />
                  <mat-error *ngIf="form.get('firstName')?.hasError('pattern'); else firstNameLengthError">{{
                    'form.errors.first_name_pattern' | translate
                  }}</mat-error>
                  <ng-template #firstNameLengthError>
                    <mat-error *ngIf="form.get('firstName')!.hasError('minlength') || form.get('firstName')!.hasError('maxlength')"
                      >{{
                        'form.errors.first_name_length'
                          | translate
                            : {
                                minLength: RULES.USER_NAME_MIN_LENGTH,
                                maxLength: RULES.USER_NAME_MAX_LENGTH,
                                currentLength: form.get('firstName')?.value?.length || 0
                              }
                      }}
                    </mat-error></ng-template
                  >
                </mat-form-field>

                <mat-form-field>
                  <mat-label>{{ 'form.fields.last_name' | translate }}</mat-label>
                  <input formControlName="lastName" matInput [placeholder]="'form.fields.last_name_placeholder' | translate" trim="blur" />
                  <mat-error *ngIf="form.get('lastName')?.hasError('pattern'); else lastNameLengthError">{{
                    'form.errors.last_name_pattern' | translate
                  }}</mat-error>
                  <ng-template #lastNameLengthError>
                    <mat-error *ngIf="form.get('lastName')!.hasError('minlength') || form.get('lastName')!.hasError('maxlength')"
                      >{{
                        'form.errors.last_name_length'
                          | translate
                            : {
                                minLength: RULES.USER_NAME_MIN_LENGTH,
                                maxLength: RULES.USER_NAME_MAX_LENGTH,
                                currentLength: form.get('lastName')?.value?.length || 0
                              }
                      }}
                    </mat-error>
                  </ng-template>
                </mat-form-field>
              </div>

              <div class="form-modal__inline-fields">
                <mat-form-field>
                  <mat-label>{{ 'form.fields.birth_date' | translate }}</mat-label>
                  <input
                    matInput
                    formControlName="birthDate"
                    [matDatepicker]="birthDatePicker"
                    (click)="birthDatePicker.open()"
                    (focus)="birthDatePicker.open()"
                    [placeholder]="'form.fields.birth_date_placeholder' | translate"
                    readonly />
                  <span matSuffix class="icon-Calendar input-icon-suffix icon-large"></span>
                  <mat-datepicker #birthDatePicker></mat-datepicker>
                </mat-form-field>
              </div>
            </div>
          </div>

          <div class="employee__section-title">{{ 'form.subtitles.main_info' | translate }}</div>

          <div class="form-modal__inline-fields">
            <mat-form-field>
              <mat-label>{{ 'form.fields.title' | translate }}</mat-label>
              <mat-select
                multiple
                formControlName="titleIds"
                [placeholder]="'form.fields.title_multiple_placeholder' | translate"
                (openedChange)="onSelectTitleOpen($event)"
                panelClass="search-dropdown">
                <div class="search-dropdown-input">
                  <input
                    [placeholder]="'form.fields.title_search' | translate"
                    (input)="filterTitle($event)"
                    [value]="searchTitleQuery" />
                </div>
                <ng-container
                  *ngIf="filteredEmployeePositionData.length > 0; else noOptions">
                  <mat-option *ngFor="let item of filteredEmployeePositionData" [value]="item.id">
                    <app-status-label [label]="item"></app-status-label>
                  </mat-option>
                </ng-container>
                <ng-template #noOptions>
                  <mat-option disabled>{{ 'messages.no_results.options' | translate }}</mat-option>
                </ng-template>
              </mat-select>
            </mat-form-field>

            <mat-form-field>
              <mat-label>{{ 'form.fields.hire_date' | translate }}</mat-label>
              <input
                matInput
                formControlName="startDate"
                [matDatepicker]="startDatePicker"
                (click)="startDatePicker.open()"
                (focus)="startDatePicker.open()"
                [placeholder]="'form.fields.hire_date_placeholder' | translate"
                readonly />
              <span matSuffix class="icon-Calendar input-icon-suffix icon-large"></span>
              <mat-datepicker #startDatePicker></mat-datepicker>
            </mat-form-field>
          </div>

          <div class="form-modal__inline-fields">
            <mat-form-field>
              <mat-label>{{ 'form.fields.employment_status' | translate }}</mat-label>
              <mat-select
                formControlName="status"
                [placeholder]="'form.fields.employment_status_placeholder' | translate"
                [compareWith]="additionalService.compareCompanySettingsOptions">
                <mat-option *ngFor="let item of companyService.employeesStatusData$ | async" [value]="additionalService.getCompanySettingsOptionValue(item)">
                  <app-status-label [label]="item"></app-status-label>
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field
              *ngIf="
                form.get('status')?.value?.systemId !== EMPLOYEE_SYSTEM_STATUS.RESIGNED && form.get('status')?.value?.systemId !== EMPLOYEE_SYSTEM_STATUS.FIRED
              ">
              <mat-label>{{ 'form.fields.availability' | translate }}</mat-label>
              <mat-select
                formControlName="availability"
                [placeholder]="'form.fields.availability_placeholder' | translate"
                [compareWith]="additionalService.compareCompanySettingsOptions">
                <mat-option *ngFor="let item of companyService.employeesAvailabilityData$ | async" [value]="additionalService.getCompanySettingsOptionValue(item)"
                  ><app-status-label [label]="item"></app-status-label>
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field
              *ngIf="
                form.get('status')?.value?.systemId == EMPLOYEE_SYSTEM_STATUS.RESIGNED || form.get('status')?.value?.systemId == EMPLOYEE_SYSTEM_STATUS.FIRED
              ">
              <mat-label>{{ 'form.fields.leave_date' | translate }}</mat-label>
              <input
                matInput
                formControlName="leaveDate"
                [matDatepicker]="leaveDatePicker"
                (click)="leaveDatePicker.open()"
                (focus)="leaveDatePicker.open()"
                [placeholder]="'form.fields.leave_date_placeholder' | translate"
                readonly />
              <span matSuffix class="icon-Calendar input-icon-suffix icon-large"></span>
              <mat-datepicker #leaveDatePicker></mat-datepicker>
            </mat-form-field>
          </div>

          <div class="form-modal__inline-fields">
            <mat-form-field>
              <mat-label>{{ 'form.fields.role' | translate }}</mat-label>
              <span matPrefix class="icon-Lock input-icon-suffix" [matTooltip]="'form.subtitles.role_visibility_notify' | translate"></span>
              <mat-select formControlName="roles" multiple [placeholder]="'form.fields.role_placeholder' | translate" panelClass="employee-roles-select">
                <mat-select-trigger>
                  {{ getSelectedRoleLabels().join(', ') }}
                </mat-select-trigger>
                <mat-option *ngFor="let role of employeeRoles" [value]="role.key" [disabled]="role.key === EMPLOYEE_ROLES.EMPLOYEE">
                  <div class="employee-roles">
                    <span class="employee-roles__name">{{ 'employee.role.' + role.value + '.name' | translate }}</span>
                    <span class="employee-roles__description">{{ 'employee.role.' + role.value + '.description' | translate }}</span>
                  </div>
                </mat-option>
              </mat-select>
              <span
                *ngIf="roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE)"
                class="icon-Question field-tooltip-icon icon-hover"
                [matTooltip]="'form.subtitles.role_notify' | translate"
                matTooltipClass="custom-tooltip-sm"></span>
            </mat-form-field>

            <mat-form-field
              *ngIf="
                form.get('status')?.value?.systemId == EMPLOYEE_SYSTEM_STATUS.RESIGNED || form.get('status')?.value?.systemId == EMPLOYEE_SYSTEM_STATUS.FIRED
              ">
              <mat-label>{{ 'form.fields.leave_reason' | translate }}</mat-label>
              <input formControlName="leaveReason" matInput [placeholder]="'form.fields.leave_reason_placeholder' | translate" trim="blur" />
              <mat-error *ngIf="form.get('leaveReason')!.hasError('maxlength')"
                >{{
                  'form.errors.field_length' | translate: { maxLength: RULES.SHORT_TEXT_MAX_LENGTH, currentLength: form.get('leaveReason')?.value?.length || 0 }
                }}
              </mat-error>
            </mat-form-field>
          </div>

          <!-- <div class="form-modal__inline-fields">
            <mat-form-field>
              <span matPrefix class="icon-Lock input-icon-suffix" [matTooltip]="'form.subtitles.agreement_type_visibility_notify' | translate"></span>
              <mat-label>{{ 'form.fields.is_fop' | translate }}</mat-label>
              <mat-select formControlName="fop" [placeholder]="'form.fields.is_fop_placeholder' | translate">
                <mat-option [value]="true">{{ 'form.fields.yes' | translate }}</mat-option>
                <mat-option [value]="false">{{ 'form.fields.no' | translate }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div> -->

          <div class="employee__section-title">{{ 'form.subtitles.managers' | translate }}</div>

          <div class="form-modal__inline-fields">
            <mat-form-field>
              <mat-label>{{ 'form.fields.primary_manager' | translate }}</mat-label>
              <mat-select
                formControlName="primaryManager"
                [placeholder]="'form.fields.primary_manager_placeholder' | translate"
                (openedChange)="onSelectPrimaryManagerOpen($event)"
                panelClass="search-dropdown">
                <div class="search-dropdown-input">
                  <input [placeholder]="'form.fields.manager_search' | translate" (input)="filterPrimaryManagers($event)" [value]="searchPrimaryManagerQuery" />
                </div>
                <ng-container *ngIf="employeePrimaryManagers.length > 0; else noPrimaryManagers">
                <mat-option *ngFor="let item of employeePrimaryManagers" [value]="item.id">
                  <app-employee-avatar [employee]="item"></app-employee-avatar>
                </mat-option>
              </ng-container>
              <ng-template #noPrimaryManagers>
                <mat-option disabled>{{ 'messages.no_results.managers' | translate }}</mat-option>
              </ng-template>
              </mat-select>
            </mat-form-field>

            <mat-form-field>
              <mat-label>{{ 'form.fields.secondary_managers' | translate }}</mat-label>
              <mat-select
                formControlName="secondaryManagers"
                [placeholder]="'form.fields.secondary_manager_placeholder' | translate"
                multiple
                (openedChange)="onSelectSecondaryManagerOpen($event)"
                panelClass="search-dropdown">
                <div class="search-dropdown-input">
                  <input
                    [placeholder]="'form.fields.manager_search' | translate"
                    (input)="filterSecondaryManagers($event)"
                    [value]="searchSecondaryManagerQuery" />
                </div>
                <ng-container *ngIf="employeeSecondaryManagers.length > 0; else noManagers">
                  <mat-option *ngFor="let item of employeeSecondaryManagers" [value]="item.id">
                    <app-employee-avatar [employee]="item"></app-employee-avatar>
                  </mat-option>
                </ng-container>
                <ng-template #noManagers>
                  <mat-option disabled>{{ 'messages.no_results.managers' | translate }}</mat-option>
                </ng-template>
              </mat-select>
              <span
                *ngIf="roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE)"
                class="icon-Question field-tooltip-icon icon-hover"
                [matTooltip]="'form.subtitles.secondary_manager_notify' | translate"
                matTooltipClass="custom-tooltip-sm"></span>
            </mat-form-field>
          </div>

          <div class="employee__section-title">{{ 'form.subtitles.contacts' | translate }}</div>

          <div class="form-modal__inline-fields">
            <mat-form-field>
              <mat-label>{{ 'form.fields.work_email' | translate }}</mat-label>
              <input formControlName="email" matInput [placeholder]="'form.fields.work_email_placeholder' | translate" trim />
              <mat-error *ngIf="form.get('email')?.hasError('email')">{{ 'form.errors.email_invalid' | translate }}</mat-error>
            </mat-form-field>

            <mat-form-field>
              <mat-label>{{ 'form.fields.personal_email' | translate }}</mat-label>
              <input formControlName="personalEmail" matInput [placeholder]="'form.fields.personal_email_placeholder' | translate" trim />
              <mat-error *ngIf="form.get('personalEmail')?.hasError('email')">{{ 'form.errors.email_invalid' | translate }}</mat-error>
            </mat-form-field>
          </div>

          <div class="form-modal__inline-fields">
            <mat-form-field>
              <mat-label>{{ 'form.fields.phone' | translate }}</mat-label>
              <input formControlName="phone" matInput [placeholder]="'form.fields.phone_placeholder' | translate" (input)="onPhoneNumberInput($event)" />
              <mat-error *ngIf="form.get('phone')?.hasError('pattern'); else phoneLengthError">{{ 'form.errors.phone_pattern' | translate }}</mat-error>
              <ng-template #phoneLengthError>
                <mat-error *ngIf="form.get('phone')?.hasError('minlength') || form.get('phone')?.hasError('maxlength')"
                  >{{ 'form.errors.phone_length' | translate: { minLength: RULES.PHONE_MIN_LENGTH - 1, maxLength: RULES.PHONE_MAX_LENGTH - 1 } }}
                </mat-error>
              </ng-template>
            </mat-form-field>

            <mat-form-field>
              <mat-label>{{ 'form.fields.country' | translate }}</mat-label>
              <mat-select
                formControlName="location"
                [placeholder]="'form.fields.country_select_placeholder' | translate"
                (openedChange)="onSelectLocationOpen($event)"
                panelClass="search-dropdown">
                <div class="search-dropdown-input">
                  <input [placeholder]="'form.fields.country_search' | translate" (input)="filterLocations($event)" [value]="searchLocationQuery" />
                </div>
                <mat-option *ngFor="let item of filteredLocations" [value]="item.id">{{ item.displayName }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <div class="form-modal__inline-fields">
            <mat-form-field>
              <mat-label>{{ 'form.fields.timeZone' | translate }}</mat-label>
              <mat-select formControlName="timeZone" [placeholder]="'form.fields.timeZone_placeholder' | translate">
                <ng-container *ngIf="(timeZonesList$ | async)?.length; else noTimeZones">
                  <mat-option *ngFor="let item of timeZonesList$ | async" [value]="item.zoneId">
                    {{ item.displayName }}
                  </mat-option>
                </ng-container>
                <ng-template #noTimeZones>
                  <mat-option disabled>
                    {{ 'form.fields.timeZone_noOptions' | translate }}
                  </mat-option>
                </ng-template>
              </mat-select>
            </mat-form-field>

            <mat-form-field>
              <mat-label>{{ 'form.fields.contact_address' | translate }}</mat-label>
              <input formControlName="contactAddress" matInput [placeholder]="'form.fields.contact_address_placeholder' | translate" trim="blur" />
            </mat-form-field>
          </div>

          <div class="employee__section-title">{{ 'form.subtitles.social_network' | translate }}</div>

          <div class="form-modal__inline-fields">
            <mat-form-field>
              <mat-label>LinkedIn</mat-label>
              <input formControlName="linkedin" matInput [placeholder]="'form.fields.linkedin_link_placeholder' | translate" trim="blur" />
              <mat-error *ngIf="form.get('linkedin')?.hasError('pattern')">{{ 'form.errors.invalid_url' | translate }}</mat-error>
            </mat-form-field>

            <mat-form-field>
              <mat-label>Instagram</mat-label>
              <input formControlName="instagram" matInput [placeholder]="'form.fields.instagram_link_placeholder' | translate" trim="blur" />
              <mat-error *ngIf="form.get('instagram')?.hasError('pattern')">{{ 'form.errors.invalid_url' | translate }}</mat-error>
            </mat-form-field>
          </div>

          <div class="form-modal__inline-fields">
            <mat-form-field>
              <mat-label>Facebook</mat-label>
              <input formControlName="facebook" matInput [placeholder]="'form.fields.facebook_link_placeholder' | translate" trim="blur" />
              <mat-error *ngIf="form.get('facebook')?.hasError('pattern')">{{ 'form.errors.invalid_url' | translate }}</mat-error>
            </mat-form-field>

            <mat-form-field>
              <mat-label>Telegram</mat-label>
              <input formControlName="telegram" matInput [placeholder]="'form.fields.telegram_link_placeholder' | translate" trim="blur" />
              <mat-error *ngIf="form.get('telegram')?.hasError('pattern')">{{ 'form.errors.telegram_pattern' | translate }}</mat-error>
            </mat-form-field>
          </div>

          <ng-container *ngIf="roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE) || projects.controls.length > 0">
            <div class="employee__section-title">{{ 'form.subtitles.projects' | translate }}</div>

            <div class="multiple-values-section" formArrayName="projects">
              <ng-container *ngFor="let project of projects.controls; let i = index">
                <div class="form-modal__inline-fields">
                  <mat-form-field [formGroupName]="i">
                    <mat-label>{{ 'form.fields.project' | translate }}</mat-label>
                    <mat-select formControlName="projectId" [placeholder]="'form.fields.project_placeholder' | translate">
                      <ng-container
                        *ngIf="(companyService.employeesProjectData$ | async) && (companyService.employeesProjectData$ | async)!.length > 0; else noOptions">
                        <mat-option *ngFor="let item of companyService.employeesProjectData$ | async" [value]="item.id">
                          <app-status-label [label]="item"></app-status-label>
                        </mat-option> </ng-container
                      ><ng-template #noOptions>
                        <mat-option disabled>{{ 'messages.no_results.options' | translate }}</mat-option>
                      </ng-template>
                    </mat-select>
                  </mat-form-field>
                  <div class="multiple-values-section__row">
                    <div class="form-modal__inline-fields" [formGroupName]="i">
                      <mat-form-field>
                        <mat-label>{{ 'form.fields.start_date' | translate }}</mat-label>
                        <input
                          matInput
                          formControlName="startDate"
                          [matDatepicker]="projectStartDatePicker"
                          (click)="projectStartDatePicker.open()"
                          (focus)="projectStartDatePicker.open()"
                          [placeholder]="'form.fields.start_date_placeholder' | translate"
                          readonly />
                        <span matSuffix class="icon-Calendar input-icon-suffix icon-large"></span>
                        <mat-datepicker #projectStartDatePicker></mat-datepicker>
                      </mat-form-field>
                      <mat-form-field>
                        <mat-label>{{ 'form.fields.end_date' | translate }}</mat-label>
                        <input
                          matInput
                          formControlName="endDate"
                          [matDatepicker]="projectEndDatePicker"
                          (click)="projectEndDatePicker.open()"
                          (focus)="projectEndDatePicker.open()"
                          [placeholder]="'form.fields.end_date_placeholder' | translate"
                          readonly />

                        <span *ngIf="!project.get('endDate')?.value" matSuffix class="icon-Calendar input-icon-suffix icon-large"></span>

                        <span
                          *ngIf="project.get('endDate')?.value"
                          (click)="clearProjectDate($event, 'endDate', i)"
                          matSuffix
                          class="icon-Close input-icon-suffix"></span>

                        <mat-datepicker #projectEndDatePicker></mat-datepicker>
                        <span
                          matSuffix
                          *ngIf="roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE)"
                          class="icon-Question field-tooltip-icon icon-hover"
                          [matTooltip]="'form.subtitles.project_end_date_notify' | translate"
                          matTooltipClass="custom-tooltip-sm"></span>
                      </mat-form-field>
                    </div>
                    <span *ngIf="roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE)" class="remove-item icon-Remove" (click)="removeProject(i)"></span>
                  </div>
                </div>
              </ng-container>

              <button
                *ngIf="roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE)"
                class="btn-main btn-secondary btn-sm btn-border"
                type="button"
                (click)="addProjects()">
                <span class="icon icon-Plus"></span> {{ 'actions.add_project' | translate }}
              </button>
            </div>
          </ng-container>

          <ng-container *ngIf="roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE) || kids.controls.length > 0">
            <div class="employee__section-title">{{ 'form.subtitles.children' | translate }}</div>

            <div class="multiple-values-section" formArrayName="kids">
              <ng-container *ngFor="let kid of kids.controls; let i = index">
                <div class="form-modal__inline-fields">
                  <mat-form-field [formGroupName]="i">
                    <mat-label>{{ 'form.fields.kid_name' | translate }}</mat-label>
                    <input formControlName="name" matInput [placeholder]="'form.fields.kid_name_placeholder' | translate" trim="blur" />
                  </mat-form-field>
                  <div class="multiple-values-section__row">
                    <div class="form-modal__inline-fields" [formGroupName]="i">
                      <mat-form-field>
                        <mat-label>{{ 'form.fields.kid_gender' | translate }}</mat-label>
                        <mat-select formControlName="gender" [placeholder]="'form.fields.kid_gender_placeholder' | translate">
                          <mat-option *ngFor="let item of KIDS_GENDER | keyvalue" [value]="item.key">{{
                            'employee.kids_gender.' + item.value | translate
                          }}</mat-option>
                        </mat-select>
                      </mat-form-field>
                      <mat-form-field>
                        <mat-label>{{ 'form.fields.kid_birth_date' | translate }}</mat-label>
                        <input
                          matInput
                          formControlName="birthDate"
                          [matDatepicker]="kidsBirthDatePicker"
                          (click)="kidsBirthDatePicker.open()"
                          (focus)="kidsBirthDatePicker.open()"
                          [placeholder]="'form.fields.kid_birth_date_placeholder' | translate"
                          readonly />
                        <span matSuffix class="icon-Calendar input-icon-suffix icon-large"></span>
                        <mat-datepicker #kidsBirthDatePicker></mat-datepicker>
                      </mat-form-field>
                    </div>
                    <span *ngIf="roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE)" class="remove-item icon-Remove" (click)="removeKid(i)"></span>
                  </div>
                </div>
              </ng-container>

              <button
                *ngIf="roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE)"
                class="btn-main btn-secondary btn-sm btn-border"
                type="button"
                (click)="addKids()">
                <span class="icon icon-Plus"></span> {{ 'actions.add_kid' | translate }}
              </button>
            </div>
          </ng-container>

          <div class="employee__section-title">{{ 'form.subtitles.need_to_know' | translate }}</div>

          <mat-form-field>
            <mat-label>{{ 'form.fields.need_to_know' | translate }}</mat-label>
            <textarea formControlName="needToKnow" matInput rows="5" [placeholder]="'form.fields.need_to_know_placeholder' | translate" trim="blur"></textarea>
            <mat-error *ngIf="form.get('needToKnow')!.hasError('maxlength')"
              >{{
                'form.errors.field_length'
                  | translate: { maxLength: RULES.MIDDLE_TEXT_MAX_LENGTH, currentLength: form.get('needToKnow')!.getError('maxlength')?.actualLength || 0 }
              }}
            </mat-error>
          </mat-form-field>

          <ng-container *ngIf="roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE)">
            <div class="divider"></div>
            <div class="employee__section-title">
              {{ 'form.subtitles.hr_notes' | translate }}
              <span class="notification"><span class="icon-Lock"></span>{{ 'form.subtitles.hr_notes_notify' | translate }}</span>
            </div>

            <mat-form-field>
              <mat-label>{{ 'form.fields.hr_notes' | translate }}</mat-label>
              <textarea formControlName="hrNote" matInput rows="5" [placeholder]="'form.fields.hr_notes_placeholder' | translate" trim="blur"></textarea>

              <mat-error *ngIf="form.get('hrNote')!.hasError('maxlength')"
                >{{
                  'form.errors.field_length'
                    | translate: { maxLength: RULES.LONG_TEXT_MAX_LENGTH, currentLength: form.get('hrNote')!.getError('maxlength')?.actualLength || 0 }
                }}
              </mat-error>
            </mat-form-field>
          </ng-container>
        </form>
      </div>
    </ng-container>
    <app-loader></app-loader>
  </mat-dialog-content>
  <mat-dialog-actions [align]="'end'">
    <button *ngIf="employee && roleAbility.can(ACTION.DELETE, SUBJECT.EMPLOYEE)" class="btn-main btn-red btn-xl" (click)="removeEmploeeModal(employee)">
      {{ 'actions.remove_employee' | translate }}
    </button>
    <button class="btn-main btn-primary btn-xl" (click)="onSubmit()" [matTooltip]="!form.valid ? ('messages.form_submit_tooltip' | translate) : null">
      {{ 'actions.save' | translate }}
    </button>
  </mat-dialog-actions>
</ng-container>
